import { LOG_EVENT_TYPES } from 'src/logger/event-types';
import { LOG_LEVEL } from 'src/logger/log-level';
import { createConsoleTransport } from 'src/logger/transports/console.transport';
import { createOtlpLogTransport } from 'src/logger/transports/otlp-log.transport';
import { createRumTransport } from 'src/logger/transports/rum.transport';

const STRUCTURED_RUM_EVENT_TYPES = Object.freeze({
    view: LOG_EVENT_TYPES.VIEW,
    operation: LOG_EVENT_TYPES.OPERATION,
    service: LOG_EVENT_TYPES.SERVICE,
    performance: LOG_EVENT_TYPES.PERFORMANCE,
});

/**
 * Sanitize a performance name into a RUM-safe event type suffix.
 *
 * @param {string} name
 * @returns {string}
 */
const toPerformanceEventType = (name) => {
    const suffix = String(name || 'unknown')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_\-./]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'unknown';
    return `${LOG_EVENT_TYPES.PERFORMANCE_PREFIX}${suffix}`;
};

/**
 * Default transports: console in non-production, RUM + OTLP logs (no-op if unset).
 *
 * @returns {Array<{ log: Function, setContext?: Function }>}
 */
export const createDefaultTransports = () => [
    createConsoleTransport(),
    createRumTransport(),
    createOtlpLogTransport(),
];

/**
 * Create a logger instance with pluggable transports.
 * Prefer the shared {@link logger} singleton in app code; use this in tests.
 *
 * @param {Object} [options]
 * @param {Array<{ log: Function, setContext?: Function }>} [options.transports]
 * @param {Object} [options.context] - Initial shared context (agency_id, etc.)
 * @returns {Object} Logger API
 */
export const createLogger = ({
    transports = createDefaultTransports(),
    context: initialContext = {},
} = {}) => {
    let context = { ...initialContext };

    const emit = ({ level, message, eventType, meta = {} }) => {
        const record = {
            level,
            message: message == null ? '' : String(message),
            eventType: eventType || undefined,
            meta: {
                ...context,
                ...meta,
            },
            timestamp: new Date().toISOString(),
        };

        transports.forEach((transport) => {
            try {
                transport.log?.(record);
            } catch (error) {
                // Never let logging break the app.
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Logger transport failed:', error);
                }
            }
        });
    };

    return {
        /**
         * Merge attributes into logger context and notify transports
         * (e.g. RUM session attributes).
         *
         * @param {Object} attributes
         */
        setContext(attributes = {}) {
            if (!attributes || typeof attributes !== 'object') {
                return;
            }

            const cleaned = Object.fromEntries(
                Object.entries(attributes).filter(([, value]) =>
                    value !== undefined && value !== null && value !== '',
                ),
            );

            if (Object.keys(cleaned).length === 0) {
                return;
            }

            context = { ...context, ...cleaned };

            transports.forEach((transport) => {
                try {
                    transport.setContext?.(cleaned);
                } catch (error) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('Logger setContext transport failed:', error);
                    }
                }
            });
        },

        /**
         * Remove keys from logger context (e.g. user identity on logout).
         *
         * @param {string[]} keys
         */
        removeContext(keys = []) {
            if (!Array.isArray(keys) || keys.length === 0) {
                return;
            }

            const removed = keys.filter((key) => key in context);
            if (removed.length === 0) {
                return;
            }

            removed.forEach((key) => {
                delete context[key];
            });

            transports.forEach((transport) => {
                try {
                    transport.removeContext?.(removed);
                } catch (error) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('Logger removeContext transport failed:', error);
                    }
                }
            });
        },

        /** @returns {Object} Shallow copy of current context */
        getContext() {
            return { ...context };
        },

        debug(message, meta) {
            emit({ level: LOG_LEVEL.DEBUG, message, meta });
        },

        info(message, meta) {
            emit({ level: LOG_LEVEL.INFO, message, meta });
        },

        warn(message, meta) {
            emit({ level: LOG_LEVEL.WARN, message, meta });
        },

        /**
         * @param {string} message
         * @param {Object} [meta] - Optional; pass event_type / eventType for catalogued beacons
         */
        error(message, meta = {}) {
            const {
                eventType: metaEventType,
                event_type: metaEventTypeSnake,
                ...rest
            } = meta;
            const eventType = metaEventType || metaEventTypeSnake;
            emit({
                level: LOG_LEVEL.ERROR,
                message,
                eventType,
                meta: rest,
            });
        },

        /**
         * Emit a structured event object or a legacy catalogued event.
         *
         * @param {Object|string} eventOrType
         * @param {Object} [fields]
         */
        event(eventOrType, fields = {}) {
            if (!eventOrType) {
                return;
            }

            if (typeof eventOrType === 'object') {
                const {
                    level = LOG_LEVEL.ERROR,
                    message,
                    event_type: structuredEventType,
                    ...meta
                } = eventOrType;

                if (!structuredEventType) {
                    return;
                }

                emit({
                    level,
                    message,
                    eventType: STRUCTURED_RUM_EVENT_TYPES[structuredEventType]
                        || LOG_EVENT_TYPES.LOG_ERROR,
                    meta: {
                        event_type: structuredEventType,
                        ...meta,
                    },
                });
                return;
            }

            const {
                level = LOG_LEVEL.ERROR,
                message,
                ...meta
            } = fields;

            emit({
                level,
                message: message != null ? message : eventOrType,
                eventType: eventOrType,
                meta,
            });
        },

        /**
         * Performance / timing beacon.
         *
         * @param {string} name - Short metric name (becomes event type suffix)
         * @param {Object} [fields]
         */
        performance(name, fields = {}) {
            emit({
                level: LOG_LEVEL.INFO,
                message: name,
                eventType: toPerformanceEventType(name),
                meta: fields,
            });
        },
    };
};

/**
 * App-wide ops logger singleton.
 * Call sites: `import { logger } from 'src/logger/logger'`.
 */
export const logger = createLogger();

export { LOG_LEVEL, LOG_LEVEL_RANK } from 'src/logger/log-level';
export { LOG_EVENT_TYPES } from 'src/logger/event-types';

/**
 * Test helper — replace the singleton's behavior by mutating exports is awkward;
 * prefer createLogger({ transports: [...] }) in unit tests. This resets context
 * on the shared singleton between tests when needed.
 */
export const __resetLoggerContextForTests = () => {
    // Recreate by clearing context via setContext overwrite is insufficient;
    // expose internal reset by reassigning methods from a fresh instance.
    const fresh = createLogger();
    Object.keys(fresh).forEach((key) => {
        logger[key] = fresh[key];
    });
};
